<script lang="ts">
  // Header Sync control — used on the Pipeline (Dashboard) and Today pages.
  // Styled per the v9 mockup's `.sync-btn-combo`. All poll state lives in
  // stores.ts so both pages share a single source of truth.
  import { pollingActive, pollProgress, pollDone, pollError, pollErrorDetail, fundSyncFilterSummary, startSync, workflow, authProvider } from './stores';
  import { getFundWorkspace, setFundAutoDrafts } from './api/fund-ir';
  import { isSupportView } from './supportView';

  let consentOpen = $state(false);
  let consentBusy = $state(false);
  let consentError = $state('');

  async function syncClick() {
    if (isSupportView()) return;
    if ($workflow !== 'fund') { startSync(); return; }
    consentBusy = true;
    consentError = '';
    try {
      const workspace = await getFundWorkspace();
      if (!workspace.preferences.auto_drafts.updated_at ||
          (workspace.preferences.auto_drafts.consent_version &&
           workspace.preferences.auto_drafts.consent_version !== workspace.preferences.auto_draft_consent_version)) consentOpen = true;
      else startSync();
    } catch {
      // Reading this choice never grants automatic drafting. A mailbox sync
      // remains available even if the disclosure status could not be loaded.
      startSync();
    } finally { consentBusy = false; }
  }

  async function chooseDrafts(enabled: boolean) {
    consentBusy = true;
    consentError = '';
    try {
      const workspace = await getFundWorkspace();
      await setFundAutoDrafts(enabled, workspace.preferences.auto_draft_consent_version);
      consentOpen = false;
      startSync();
    } catch (err: any) {
      consentError = err?.message || 'Could not save your choice. Try again.';
    } finally { consentBusy = false; }
  }

  function pct(p: { total: number; processed: number } | null): number {
    if (!p || !p.total) return 0;
    return Math.min(100, Math.round((p.processed / p.total) * 100));
  }

  function phaseLabel(p: NonNullable<typeof $pollProgress>): string {
    switch (p.phase) {
      case 'waiting': return 'Syncing emails…';
      case 'fetching': return `Fetching changes from ${$authProvider === 'microsoft' ? 'Outlook' : 'Gmail'}…`;
      case 'fund_sync': return `Scanning mailbox… ${p.clients} contacts found across ${p.pages || 0} pages`;
      case 'fund_classification': return `Sorting relationships… ${p.processed}/${p.total} contacts`;
      case 'rate_limited': return 'Rate limited — retrying soon…';
      case 'analyzing': return `Analyzing ${p.clients}/${p.clients_total || '?'} clients`;
      case 'classifying': return `Classifying ${p.processed}/${p.total} threads`;
      case 'done': return p.clients ? `Done — ${p.clients} ${$workflow === 'fund' ? 'contacts' : 'clients'}` : 'Up to date';
      default: return `Building ${p.processed}/${p.total} threads`;
    }
  }
</script>

<div class="sync-wrap">
  <button class="sync-btn-combo" onclick={syncClick} disabled={$pollingActive || consentBusy || isSupportView()}>
    {#if $pollingActive}
      <span class="spinner sync-spinner"></span>
      <span>Syncing…</span>
    {:else}
      <span class="sync-dot" class:err={$pollDone && $pollError}></span>
      <span>{isSupportView() ? 'View only' : 'Sync'}</span>
      <span class="sync-icon">↻</span>
    {/if}
  </button>
  {#if consentOpen}
    <div class="sync-consent" role="dialog" aria-label="Automatic review drafts">
      <strong>Prepare review drafts automatically?</strong>
      <p>Optional. For confirmed LPs with a direct reply or follow-up signal, Dealton sends up to eight relevant messages from your mailbox (up to 1,500 characters each) to Anthropic. Drafts appear in Today for your review. Dealton never sends them automatically or includes Fund documents. You can turn this off in Settings. <a href="https://dealton.ai/terms.html" target="_blank" rel="noopener">Terms</a> · <a href="https://dealton.ai/privacy.html" target="_blank" rel="noopener">Privacy</a>.</p>
      {#if consentError}<p class="sync-consent-error" role="alert">{consentError}</p>{/if}
      <div><button type="button" onclick={() => chooseDrafts(false)} disabled={consentBusy}>Sync only</button><button type="button" onclick={() => chooseDrafts(true)} disabled={consentBusy}>Enable drafts &amp; sync</button></div>
    </div>
  {/if}
  {#if $pollingActive && $pollProgress}
    <div class="sync-progress">
      <div class="sync-bar">
        <div
          class="sync-bar-fill"
          class:summarizing={$pollProgress.phase === 'summarizing'}
          style="width:{pct($pollProgress)}%;"
        ></div>
      </div>
      <div class="sync-progress-label">{phaseLabel($pollProgress)}</div>
    </div>
  {:else if $pollDone}
    <span class="sync-status" class:err={$pollError}>
      {$pollError ? ($pollErrorDetail || 'Sync failed') : 'Sync complete'}
    </span>
  {/if}
  {#if $workflow === 'fund' && !$pollingActive && !$pollError && $fundSyncFilterSummary}
    <span class="sync-filter-note" title={$fundSyncFilterSummary}>{$fundSyncFilterSummary}</span>
  {/if}
</div>

<style>
  .sync-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  .sync-consent { position: fixed; z-index: 100; top: 72px; right: 24px; width: min(410px, calc(100vw - 48px)); padding: 16px; border: 1px solid var(--border-strong); border-radius: var(--radius-sm); background: var(--bg-surface); color: var(--text); box-shadow: 0 16px 48px rgba(0,0,0,.35); }
  .sync-consent strong { font-size: 14px; }
  .sync-consent p { font-size: 12px; line-height: 1.5; color: var(--text-muted); }
  .sync-consent a { color: var(--accent); }
  .sync-consent > div { display: flex; gap: 8px; justify-content: flex-end; }
  .sync-consent button { font: inherit; font-size: 12px; cursor: pointer; border: 1px solid var(--border-strong); border-radius: var(--radius-sm); padding: 7px 10px; color: var(--text); background: var(--bg-hover); }
  .sync-consent button:last-child { background: var(--accent); color: white; border-color: var(--accent); }
  .sync-consent button:disabled { opacity: .5; cursor: default; }
  .sync-consent .sync-consent-error { color: var(--red); }
  /* ── Sync button — mirrors v9 mockup `.sync-btn-combo` ── */
  .sync-btn-combo {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 6px;
    font-size: 12px;
    color: var(--text-muted);
    cursor: pointer;
    font-family: inherit;
    white-space: nowrap;
    transition: all 0.12s;
  }
  .sync-btn-combo:hover:not(:disabled) {
    background: var(--bg-hover);
    color: var(--text);
    border-color: var(--border-strong);
  }
  .sync-btn-combo:disabled { cursor: default; opacity: 0.9; }
  .sync-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: var(--green, #4ADE80);
    flex-shrink: 0;
  }
  .sync-dot.err { background: var(--red, #F26C6C); }
  .sync-icon { color: var(--text-dim); font-size: 12px; }
  .sync-btn-combo:hover:not(:disabled) .sync-icon { color: var(--text); }
  .sync-spinner { width: 12px; height: 12px; border-width: 2px; }

  /* ── Progress bar ── */
  .sync-progress {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 140px;
    max-width: 220px;
  }
  .sync-bar {
    background: var(--border);
    border-radius: 3px;
    height: 4px;
    overflow: hidden;
  }
  .sync-bar-fill {
    background: var(--accent);
    height: 100%;
    transition: width 0.3s;
  }
  .sync-bar-fill.summarizing { background: var(--green, #4ADE80); }
  .sync-progress-label {
    font-size: 10.5px;
    color: var(--text-dim);
    font-family: var(--font-mono);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sync-status {
    font-size: 11px;
    color: var(--green, #4ADE80);
  }
  .sync-status.err { color: var(--red, #F26C6C); }
  .sync-filter-note {
    color: var(--text-dim);
    font-size: 10px;
    max-width: 250px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
